import { createElement, init } from '@airwallex/components-sdk';
import { useEffect, useState } from 'react';
import {
  postToNative,
  type ScaBridgeEvent,
  type ScaInitParams,
  type ScaVerifyElement,
} from './bridge';

/**
 * Standalone Airwallex `scaVerify` host for the mobile WebView. It waits for
 * native to inject the SCA params, runs the embedded SDK element, and forwards
 * lifecycle events to native. It owns no auth logic: native performs authorize
 * and the PAN reveal/retry, and re-injects fresh params after `setup_complete`.
 */
export const ScaWrapperPage = () => {
  const [params, setParams] = useState<ScaInitParams | null>(null);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  // Expose the native -> page entry point and announce readiness so native knows
  // when it can inject the params (and re-inject after `setup_complete`).
  useEffect(() => {
    window.AirwallexScaBridge = { init: (next) => setParams(next) };
    postToNative({ type: 'bridge_ready' });

    return () => {
      window.AirwallexScaBridge = undefined;
    };
  }, []);

  useEffect(() => {
    if (!params || !target) {
      return;
    }

    const {
      env,
      clientId,
      codeVerifier,
      authCode,
      userEmail,
      scaSessionCode,
      theme,
    } = params;

    let element: ScaVerifyElement | undefined;
    let cancelled = false;
    let settled = false;
    let errorReported = false;

    // Terminal outcomes end the session; the first one wins. Only an actual
    // token success, a user cancel, or an init failure are terminal.
    const settle = (event: ScaBridgeEvent) => {
      if (settled) {
        return;
      }
      settled = true;
      postToNative(event);
    };

    // Non-terminal signals (e.g. a wrong passcode) are forwarded for UX and
    // telemetry but must never latch: the element can stay mounted and the user
    // may retry and succeed in the same session, so a later `verificationSucceed`
    // must still get through.
    const report = (event: ScaBridgeEvent) => {
      if (settled) {
        return;
      }
      postToNative(event);
    };

    const setup = async () => {
      await init({
        env,
        clientId,
        codeVerifier,
        authCode,
        enabledElements: ['risk'],
      });

      if (cancelled) {
        return;
      }

      element = (await createElement('scaVerify', {
        userEmail,
        scaSessionCode,
        disableFullPage: true,
        theme,
      })) as unknown as ScaVerifyElement;

      // createElement is async, so the page may have re-initialised while it was
      // pending; tear down the now-orphaned element.
      if (cancelled) {
        element.destroy();
        return;
      }

      element.on('ready', () => postToNative({ type: 'ready' }));
      element.on('verificationSucceed', (data) =>
        data?.token
          ? settle({ type: 'verificationSucceed', token: data.token })
          : report({ type: 'missing_token' })
      );
      element.on('verificationFailed', (data) =>
        report({ type: 'verificationFailed', reason: data?.reason })
      );
      element.on('cancel', () => settle({ type: 'cancel' }));
      // The SDK fires `error` repeatedly for one logical failure (one per failed
      // internal call); forward only the first, and don't latch the session so a
      // subsequent retry success still reaches native.
      element.on('error', (data) => {
        if (errorReported) {
          return;
        }
        errorReported = true;
        report({ type: 'error', message: data?.message });
      });

      // First-time enrolment finishes here with no token and does not roll on to
      // verification. Not terminal: native re-authorizes and calls init() again.
      const handleSetupComplete = () =>
        postToNative({ type: 'setup_complete' });
      element.on('scaSetupSucceed', handleSetupComplete);
      element.on('resetPasscodeSucceed', handleSetupComplete);

      element.mount(target);
    };

    // Ignore rejections from a run that was already torn down by a re-init; a
    // late failure from an abandoned attempt must not mark a healthy session
    // failed.
    setup().catch(() => {
      if (cancelled) {
        return;
      }
      settle({ type: 'init_failed' });
    });

    return () => {
      cancelled = true;
      element?.destroy();
    };
  }, [params, target]);

  return (
    <div
      ref={setTarget}
      data-testid="awx-sca-wrapper"
      style={{ width: '100%' }}
    />
  );
};
